import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { jwtDecode } from 'jwt-decode'

import { getBrowserCookie, setBrowserCookie } from 'lib/utils'

import { COOKIES_INTERCOM_TOKEN } from 'lib/constants'

import { REFRESH_INTERCOM_TOKEN_MUTATION } from 'gql/mutations/intercom'

const TOKEN_REFRESH_BUFFER_MINUTES = 10
const MINUTES_TO_SECONDS = 60
const SECONDS_TO_MILLISECONDS = 1000

const useIntercomToken = ({ isLoggedIn }: { isLoggedIn: boolean }) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [intercomToken, setIntercomToken] = useState<string | null>(
    () => getBrowserCookie(COOKIES_INTERCOM_TOKEN) || null
  )

  const [refreshIntercomToken] = useMutation(REFRESH_INTERCOM_TOKEN_MUTATION.mutation, {
    onCompleted: (data) => {
      const { intercomJwt } = data[REFRESH_INTERCOM_TOKEN_MUTATION.mutationName]
      setIntercomToken(intercomJwt)
      setBrowserCookie(COOKIES_INTERCOM_TOKEN, intercomJwt)
    },
  })

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // schedule refresh intercom token just before 10 minutes of expiry
  const scheduleRefreshIntercomToken = useCallback(
    ({
      expiryInSeconds,
      currentTimeInSeconds,
    }: {
      expiryInSeconds: number
      currentTimeInSeconds: number
    }) => {
      clearTimeoutRef()

      const bufferSeconds = TOKEN_REFRESH_BUFFER_MINUTES * MINUTES_TO_SECONDS
      const refreshTime = expiryInSeconds - bufferSeconds
      const delaySeconds = refreshTime - currentTimeInSeconds

      timeoutRef.current = setTimeout(() => {
        refreshIntercomToken()
      }, delaySeconds * SECONDS_TO_MILLISECONDS)
    },
    [refreshIntercomToken, clearTimeoutRef]
  )

  useEffect(() => {
    if (intercomToken && isLoggedIn) {
      const decoded = jwtDecode(intercomToken)
      const { exp } = decoded

      if (!exp) return

      const currentTime = Math.floor(Date.now() / SECONDS_TO_MILLISECONDS)
      const isExpired = exp < currentTime
      if (isExpired) {
        refreshIntercomToken()
        return
      }
      scheduleRefreshIntercomToken({ expiryInSeconds: exp, currentTimeInSeconds: currentTime })
    } else if (isLoggedIn) {
      refreshIntercomToken()
    }
  }, [intercomToken, refreshIntercomToken, scheduleRefreshIntercomToken, isLoggedIn])

  useEffect(() => {
    return () => {
      clearTimeoutRef()
    }
  }, [clearTimeoutRef])

  useEffect(() => {
    if (!isLoggedIn) setIntercomToken(null)
  }, [isLoggedIn])

  return { intercomToken }
}

export { useIntercomToken }
